import { useState, useEffect, useRef, useCallback } from 'react';
import Icon from '@material-ui/core/Icon';
import { apiClient } from '@services/api';
import { useHistory } from 'react-router-dom';

const POLL_INTERVAL = 30000; // 30s

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef(null);
  const history = useHistory();
  const [alertModal, setAlertModal] = useState(null); // notificação logistics_alert aberta no modal

  // Modo ALERTA (2026-06-05): alerta de logística não lido → sino vermelho pulsando
  // com "!" — não pode se misturar com notificações comuns (risco de deixar cliente p/ trás)
  const hasCriticalUnread = notifications.some(n => !n.read && n.type === 'logistics_alert');
  // Liberação de cupos pendente → sino AMARELO (2026-06-05). Crítico (vermelho) tem prioridade.
  const hasSeatRequestUnread = notifications.some(n => !n.read && n.type === 'seat_request');
  // Sino VERDE = tudo tranquilo (2026-07-02): nada não-lido e sem alerta de logística,
  // a qualquer hora. Vermelho (crítico) e amarelo (cupos) têm prioridade — como exigem
  // não-lido, são mutuamente exclusivos com o verde (que só aparece com 0 não-lidos).
  const allClear = unreadCount === 0 && !hasCriticalUnread && !hasSeatRequestUnread;

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchNotifications = useCallback(() => {
    apiClient.httpInstance
      .get('/notifications')
      .then(res => {
        if (res.status === 200) {
          setNotifications(res.data.data ?? []);
          setUnreadCount(res.data.unread_count ?? 0);
        }
      })
      .catch(() => {});
  }, []);

  // Polling
  useEffect(() => {
    fetchNotifications();
    const id = setInterval(fetchNotifications, POLL_INTERVAL);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  // Fechar ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Actions ────────────────────────────────────────────────────────────────

  const markRead = (id) => {
    apiClient.httpInstance
      .post(`/notifications/${id}/read`)
      .then(() => fetchNotifications())
      .catch(() => {});
  };

  const markAllRead = () => {
    apiClient.httpInstance
      .post('/notifications/read-all')
      .then(() => fetchNotifications())
      .catch(() => {});
  };

  const handleClick = (n) => {
    if (!n.read) markRead(n.id);
    // Alerta de logística → modal com a lista completa de problemas (2026-06-05)
    if (n.type === 'logistics_alert') {
      setAlertModal(n);
      setOpen(false);
      return;
    }
    // Liberação de cupos → abre o widget flutuante na aba certa (2026-06-05)
    if (n.type === 'seat_request' || n.type === 'seat_request_reviewed') {
      window.dispatchEvent(new CustomEvent('ft-open-seat-requests', {
        detail: { tab: n.type === 'seat_request_reviewed' ? 'mine' : 'approve' },
      }));
      setOpen(false);
      return;
    }
    // CSAT Viagem: nota baixa → leva direto pra tela de avaliações (2026-08-28)
    if (n.type === 'low_rating') {
      history.push('/admin/csat-viagem');
      setOpen(false);
      return;
    }
    const ticketId  = n.data?.ticket_id;
    const presaleId = n.data?.presale_id;
    if (presaleId) {
      history.push(`/admin/orcamentos/detalhe/${presaleId}`);
    } else if (ticketId) {
      history.push(`/admin/tickets/${ticketId}`);
    } else if (n.type && n.type.startsWith('ticket')) {
      history.push('/admin/tickets');
    }
    setOpen(false);
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const typeIcon = (type) => {
    switch (type) {
      case 'ticket_new':      return { icon: 'confirmation_number', color: '#38bdf8' };
      case 'ticket_reply':    return { icon: 'reply',               color: '#a855f7' };
      case 'ticket_resolved': return { icon: 'check_circle',        color: '#22c55e' };
      case 'presale_comment': return { icon: 'chat_bubble',         color: '#62DEC2' };
      case 'payment_urgent': return { icon: 'payments',             color: '#f97316' };
      case 'approval_request':return { icon: 'sell',                color: '#f59e0b' };
      // Pedido de estorno (2026-08-11) — cor própria (rosa), não usada por nenhum outro
      // tipo, pra não confundir com desconto gerencial (âmbar) nem urgência de pagamento
      // (laranja). Some sozinho quando marcado como lida — sino volta a ficar verde com
      // tudo lido, mesmo comportamento padrão de qualquer outra notificação.
      case 'refund_request':  return { icon: 'money_off',           color: '#ec4899' };
      case 'logistics_alert': return { icon: 'warning',             color: '#ef4444' };
      case 'seat_request':          return { icon: 'event_seat',    color: '#facc15' };
      case 'seat_request_reviewed': return { icon: 'event_seat',    color: '#62DEC2' };
      case 'presale_viewed':  return { icon: 'visibility',          color: '#38bdf8' };
      case 'presale_checkout':return { icon: 'shopping_cart',       color: '#22c55e' };
      case 'low_rating':      return { icon: 'star',                color: '#F5C842' };
      default:                return { icon: 'notifications',        color: '#94a3b8' };
    }
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-flex' }}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          position: 'relative',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '6px',
          borderRadius: '8px',
          color: open ? '#e2e8f0' : '#8ba5c4',
          display: 'flex',
          alignItems: 'center',
          transition: 'color 0.15s',
        }}
        title="Notificações"
      >
        <Icon style={{
          fontSize: '22px',
          color: hasCriticalUnread ? '#ef4444' : (hasSeatRequestUnread ? '#facc15' : (allClear ? '#4ade80' : undefined)),
          animation: hasCriticalUnread ? 'ftBellPulse 1s ease-in-out infinite' : 'none',
        }}>{hasCriticalUnread ? 'notification_important' : 'notifications'}</Icon>
        {hasCriticalUnread && (
          <style>{`@keyframes ftBellPulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.18); opacity: .75; } }`}</style>
        )}
        {hasCriticalUnread ? (
          <span style={{
            position: 'absolute',
            top: '0px',
            right: '0px',
            minWidth: '18px',
            height: '18px',
            borderRadius: '9px',
            background: '#ef4444',
            color: '#fff',
            fontSize: '13px',
            fontWeight: 900,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 3px',
            lineHeight: 1,
            boxShadow: '0 0 0 2px #0a1f3c',
          }}>
            !
          </span>
        ) : unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '2px',
            right: '2px',
            minWidth: '16px',
            height: '16px',
            borderRadius: '8px',
            background: '#ef4444',
            color: '#fff',
            fontSize: '10px',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 3px',
            lineHeight: 1,
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          right: 0,
          width: '340px',
          background: '#0f2340',
          border: '1px solid #1e3a5f',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          zIndex: 9999,
          overflow: 'hidden',
        }}>
          {/* Header do dropdown */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid #1e3a5f',
          }}>
            <span style={{ color: '#e2e8f0', fontWeight: 600, fontSize: '14px' }}>
              Notificações {unreadCount > 0 && (
                <span style={{
                  background: '#ef4444',
                  color: '#fff',
                  borderRadius: '10px',
                  padding: '1px 7px',
                  fontSize: '11px',
                  marginLeft: '6px',
                }}>
                  {unreadCount}
                </span>
              )}
            </span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#38bdf8',
                  fontSize: '12px',
                  padding: '2px 4px',
                }}
              >
                Marcar todas como lidas
              </button>
            )}
          </div>

          {/* Lista */}
          <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{
                padding: '32px 16px',
                textAlign: 'center',
                color: '#4a6080',
                fontSize: '13px',
              }}>
                <Icon style={{ fontSize: '32px', marginBottom: '8px', display: 'block' }}>
                  notifications_none
                </Icon>
                Nenhuma notificação ainda.
              </div>
            ) : (
              [...notifications].sort((a, b) =>
                ((!b.read && b.type === 'logistics_alert') ? 1 : 0) - ((!a.read && a.type === 'logistics_alert') ? 1 : 0)
              ).slice(0, 10).map(n => {
                const { icon, color } = typeIcon(n.type);
                const isCritical = !n.read && n.type === 'logistics_alert';
                return (
                  <div
                    key={n.id}
                    onClick={() => handleClick(n)}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      padding: '12px 16px',
                      cursor: (n.data?.ticket_id || n.data?.presale_id) ? 'pointer' : 'default',
                      background: isCritical ? 'rgba(239,68,68,0.10)' : (n.read ? 'transparent' : 'rgba(56,189,248,0.06)'),
                      borderBottom: '1px solid rgba(30,58,95,0.5)',
                      borderLeft: isCritical ? '3px solid #ef4444' : '3px solid transparent',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => {
                      if (n.data?.ticket_id || n.data?.presale_id) e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = n.read ? 'transparent' : 'rgba(56,189,248,0.06)';
                    }}
                  >
                    {/* Ícone */}
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      background: `${color}22`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Icon style={{ fontSize: '16px', color }}>{icon}</Icon>
                    </div>

                    {/* Texto */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        color: n.read ? '#94a3b8' : '#e2e8f0',
                        fontSize: '13px',
                        fontWeight: n.read ? 400 : 600,
                        lineHeight: '1.3',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {n.title}
                      </div>
                      {n.body && (
                        <div style={{
                          color: '#64748b',
                          fontSize: '12px',
                          marginTop: '2px',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                          {n.body}
                        </div>
                      )}
                      <div style={{
                        color: '#475569',
                        fontSize: '11px',
                        marginTop: '4px',
                      }}>
                        {n.created_at}
                      </div>
                    </div>

                    {/* Dot não lida */}
                    {!n.read && (
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#38bdf8',
                        flexShrink: 0,
                        marginTop: '6px',
                      }} />
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer — link para todos os tickets */}
          {notifications.length > 0 && (
            <div
              onClick={() => { history.push('/admin/tickets'); setOpen(false); }}
              style={{
                padding: '10px 16px',
                textAlign: 'center',
                color: '#38bdf8',
                fontSize: '13px',
                cursor: 'pointer',
                borderTop: '1px solid #1e3a5f',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(56,189,248,0.06)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              Ver todos os tickets →
            </div>
          )}
        </div>
      )}

      {/* Modal de detalhe do alerta de logística (2026-06-05) */}
      {alertModal && (
        <div
          onClick={() => setAlertModal(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 10000,
            background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#0f2340',
              border: '1px solid #ef4444',
              borderRadius: '14px',
              width: '92%', maxWidth: '560px',
              maxHeight: '80vh', overflowY: 'auto',
              boxShadow: '0 16px 48px rgba(0,0,0,0.6)',
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 18px', borderBottom: '1px solid rgba(239,68,68,0.4)',
              position: 'sticky', top: 0, background: '#0f2340',
            }}>
              <span style={{ color: '#ef4444', fontWeight: 700, fontSize: '15px' }}>
                {alertModal.title}
              </span>
              <Icon
                onClick={() => setAlertModal(null)}
                style={{ cursor: 'pointer', color: '#94a3b8', fontSize: '20px' }}
              >close</Icon>
            </div>
            <div style={{ padding: '14px 18px' }}>
              {(alertModal.data?.alerts?.length ? alertModal.data.alerts : (alertModal.body || '').split('\n')).map((a, i) => (
                <div key={i} style={{
                  padding: '9px 12px', marginBottom: '6px',
                  background: 'rgba(239,68,68,0.08)',
                  border: '1px solid rgba(239,68,68,0.25)',
                  borderRadius: '8px',
                  color: '#e2e8f0', fontSize: '13px', lineHeight: 1.5,
                }}>
                  {a}
                </div>
              ))}
              <div style={{ color: '#94a3b8', fontSize: '11px', marginTop: '10px' }}>
                Verifique a tela de Saídas{alertModal.data?.date ? ` do dia ${alertModal.data.date.split('-').reverse().join('/')}` : ''} e ajuste as alocações.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
